import React, { useEffect, useState, useRef  } from 'react';
import api from '../../../shared/api/axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import LikeListModal from './LikeListModal'; 
import LazyMedia from '../Gallery/LazyMediaMobile';
import { useTranslation } from 'react-i18next';
import SmartVideoPlayerGallery from '../Gallery/SmartVideoPlayerMyPostings';

type MediaEntry =
  | { kind: 'image'; url: string }
  | {
      kind: 'video';
      videoId?: string | null;
      hlsUrl?: string | null;
      dashUrl?: string | null;
      posterUrl?: string | null;
      musicId?: string | null;
      musicTitle?: string | null;
      musicArtist?: string | null;
    };


type MusicInfo = {
  musicId?: string | null;
  musicTitle?: string | null;
  musicArtist?: string | null;
};


interface Post {
  _id: string;
  type: 'media';
  title: string;
  titleColor?: string;
  media: MediaEntry[];
  likes: number;
  likedBy?: string[];
  location?: {
    city: string;
    country: string;
  };
  createdAt: string;
  isHidden?: boolean;
  hiddenAt?: string | null;
}

// Api base URL normalization
const IMAGE_CDN = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL || '';

const toImageUrl = (p: any): string => {
  const s = String(p || '').trim();
  if (!s) return '';
  if (/^(https?:|data:)/i.test(s)) return s;
  return `${IMAGE_CDN.replace(/\/$/, '')}/${s.replace(/^\/+/, '')}`;
};

const API_BASE = process.env.REACT_APP_API_BASE_URL!;
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

const hlsUrl = (videoId: string) =>
  `${API_ORIGIN}/hls/playlist/${videoId}`;

const resolveVideoUrl = (m: Extract<MediaEntry, { kind: 'video' }>): string => {
  if (m.hlsUrl) return m.hlsUrl;
  if (m.videoId) return hlsUrl(m.videoId);
  return '';
};


const mediaLabelOf = (m: MediaEntry): string => {
  if (m.kind !== 'video') return '';
  const t = (m.musicTitle || '').trim();
  const a = (m.musicArtist || '').trim();
  if (!t && !a) return '';
  return t && a ? `${t} — ${a}` : (t || a);
};


const normalizeMediaToEntries = (p: any): MediaEntry[] => {
  const src = Array.isArray(p?.media)
    ? p.media
    : Array.isArray(p?.mediaUrls)
    ? p.mediaUrls
    : [];

  return src
    .map((m: any) => {
      // IMAGE STRING
      if (typeof m === 'string') {
        return { kind: 'image', url: toImageUrl(m) };
      }

      // VIDEO — new pipeline
      if (m?.kind === 'video' || m?.isHls || m?.videoId || m?.hlsUrl || m?.dashUrl) {
        return {
          kind: 'video',
          videoId: m.videoId ?? null,
          hlsUrl: m.hlsUrl ?? null,
          dashUrl: m.dashUrl ?? null,
          posterUrl: m.posterUrl ?? null,
          musicId: m.musicId ?? m.music?._id ?? m.music?.id ?? null,
          musicTitle: m.musicTitle ?? m.music?.title ?? null,
          musicArtist: m.musicArtist ?? m.music?.artist ?? null,
        };
      }

      // IMAGE OBJECT
      const img = m?.url || m?.image?.url || m?.src || m?.path || null;
      if (!img) return null;

      return {
        kind: 'image',
        url: toImageUrl(img),
      };
    })
    .filter(Boolean) as MediaEntry[];
};

   
// --- Orientation helpers (image+video) ---

function useMediaOrientation(url: string, isVideo: boolean) {
  const [isLandscape, setIsLandscape] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    let alive = true;

    if (!url) {
      setIsLandscape(null);
      return;
    }

    if (isVideo) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = url;

      const onMeta = () => {
        if (!alive) return;
        const w = v.videoWidth || 0;
        const h = v.videoHeight || 0;
        setIsLandscape(w && h ? w > h : null);
        cleanup();
      };
      const onErr = () => { if (alive) setIsLandscape(null); cleanup(); };
      const cleanup = () => {
        v.removeEventListener("loadedmetadata", onMeta);
        v.removeEventListener("error", onErr);
      };

      v.addEventListener("loadedmetadata", onMeta);
      v.addEventListener("error", onErr);
      try { v.load(); } catch {}
      return () => { alive = false; cleanup(); };
    }

    // image
    const img = new Image();
    img.onload = () => {
      if (!alive) return;
      setIsLandscape(img.naturalWidth > img.naturalHeight);
    };
    img.onerror = () => { if (alive) setIsLandscape(null); };
    img.src = url;

    return () => { alive = false; };
  }, [url, isVideo]);

  return isLandscape;
}

// --- Auto-fit renderer for one media item ---
const AutoFitMedia: React.FC<{
     url: string;
     isVideo: boolean;
     onClick?: () => void;
     label?: string;
     onRemove?: () => void;
     showMusic?: boolean;                  // control visibility (hover)
     musicId?: string | null;
     musicTitle?: string | null;
     musicArtist?: string | null;
     onMusicClick?: (info: MusicInfo) => void | Promise<void>;
     onMouseEnter?: () => void;   
     onMouseLeave?: () => void;

   }> = ({ url, isVideo, onClick, label, onRemove, showMusic, musicId, musicTitle, musicArtist, onMusicClick, onMouseEnter, onMouseLeave }) => {
  const isVid = isVideo;
  const { t } = useTranslation();
const [isLoading, setIsLoading] = React.useState(true);

  const isLandscape = useMediaOrientation(url, isVid);

  const portraitFrame: React.CSSProperties = {
  width: '100%',
  height: '100%',
  maxHeight: MEDIA_HEIGHT,
  objectFit: 'contain',
  borderRadius: 12,
  display: 'block',
  backgroundColor: '#000',
};

const landscapeFrame: React.CSSProperties = {
  width: '100%',
  height: '100%',
  maxHeight: LAND_MAX_HEIGHT,
  objectFit: 'contain',
  borderRadius: 12,
  display: 'block',
  backgroundColor: '#000',
};


  const tileStyle: React.CSSProperties = {
  position: 'relative',
  flex: '0 0 auto',
  width: 'min(92vw, 520px)',
  margin: '0 20px', 
  height: MEDIA_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#000',
  borderRadius: 12,
};


  const frameStyle = isLandscape === false ? portraitFrame : landscapeFrame;

  // NEW: inner wrapper that shrink-wraps to the media box
  const innerWrapStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-block', // shrink to media’s rendered width
    maxWidth: '100%',
    lineHeight: 0,           // remove inline-block gaps
  };

  const removeBtnStyle: React.CSSProperties = {
    position: 'absolute',
    top: 8,
    right: 8, // always 8px from the media’s edge
    width: 34,
    height: 34,
    borderRadius: '9999px',
    border: '1px solid rgba(255,255,255,0.25)',
    background: 'rgba(34, 34, 39, 0.85)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2,
    boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
    lineHeight: 1,
  };

  const xSvg = (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12"
            stroke="currentColor" strokeWidth="2.25" strokeLinecap="round"/>
    </svg>
  );

 return (
  <div
    style={tileStyle}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >

      <div style={innerWrapStyle}>
        {isVid ? (
<div
  style={{
    position: 'relative',
    width: '100%',
    height: isLandscape === false ? MEDIA_HEIGHT : 'auto',
    maxHeight: MEDIA_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}
>
  <SmartVideoPlayerGallery
    videoUrl={url}
    muted
    preload="metadata"
  style={
  isLandscape === false
    ? {
        width: '100%',
        height: MEDIA_HEIGHT,   // 🔥 portrait fills frame
        objectFit: 'contain',
        borderRadius: 12,
        display: 'block',
      }
    : {
        width: '100%',
        height: 'auto',         // 🔥 landscape shrink-wrap
        maxHeight: MEDIA_HEIGHT,
        objectFit: 'contain',
        borderRadius: 12,
        display: 'block',
      }
}

  />
</div>


        ) : (
          <LazyMedia
  src={url}
  type="img"
  alt="media"
  style={{ ...frameStyle, cursor: onClick ? 'pointer' : undefined }}
  onClick={onClick}
/>
        )}

        {/* remove button now anchored to the media’s box */}
        {onRemove && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            aria-label={t('myPosts.media.remove')}
            title={t('myPosts.media.remove')}
            style={removeBtnStyle}
          >
  <span style={{ fontSize: 19, fontWeight: 400, }}>×</span>
</button>
        )}

        {/* clickable music pill; visible on hover if showMusic=true */}
        {isVid && !!label && (
          <div
            title={label}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: -13,
              padding: '6px 10px 10px',
              fontSize: 13,
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              background:
                'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.6) 100%)',
              pointerEvents: 'auto',
              opacity: showMusic || 'ontouchstart' in window ? 1 : 0,
              transition: 'opacity 220ms ease',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <span aria-hidden="true" style={{ flex: '0 0 auto' }}>🎵</span>
            {musicId ? (
              <Link
                to={`/musicdetails/${musicId}`}
                style={{
                  minWidth: 0,
                  color: '#c1c9d6',
                  fontWeight: 600,
                  textDecoration: 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={
                  musicTitle
                    ? t('myPosts.media.openDetails', { title: musicTitle })
                    : t('myPosts.media.openUnknownDetails')
                }
                onClick={(e) => e.stopPropagation()}
              >
                {label}
              </Link>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMusicClick?.({
  musicId: musicId ?? null,
  musicTitle: musicTitle ?? null,
  musicArtist: musicArtist ?? null,
});

                }}
                style={{
                  minWidth: 0,
                  color: '#71757b',
                  fontWeight: 500,
                  background: 'transparent',
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={
                  musicTitle
                    ? t('myPosts.media.openDetails', { title: musicTitle })
                    : t('myPosts.media.openUnknownDetails')
                }
              >
                {label}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// Limiting Memory
const WINDOW_BEFORE = 1;
const WINDOW_AFTER = 2;
const MAX_POSTS_IN_MEMORY = 60;


const MyPostingsMobile = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [likesModalPostId, setLikesModalPostId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const LIMIT = 10;
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const location = useLocation();
  const focusPostId = (location.state as any)?.focusPostId;

  const [initialLoading, setInitialLoading] = useState(true);

  const [deleteTargetPost, setDeleteTargetPost] = useState<Post | null>(null);
const [deleteBusy, setDeleteBusy] = useState(false);
const [activeIndex, setActiveIndex] = useState(0);

const loadMoreRef = useRef<HTMLDivElement | null>(null);
const feedRef = useRef<HTMLDivElement | null>(null);
const prefetchedUrlsRef = useRef(new Set<string>());

  // Music Info
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
const hoverTimeoutRef = useRef<number | null>(null);

const handleHoverEnter = (key: string) => {
  if (hoverTimeoutRef.current) {
    window.clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = null;
  }
  setHoveredKey(key);
};
const handleHoverLeave = () => {
  hoverTimeoutRef.current = window.setTimeout(() => {
    setHoveredKey(null);
    hoverTimeoutRef.current = null;
  }, 500);
};
useEffect(() => () => {
  if (hoverTimeoutRef.current) window.clearTimeout(hoverTimeoutRef.current);
}, []);

// Only one Video at a time
useEffect(() => {
  const onPlay = (e: Event) => {
    const el = e.target as HTMLVideoElement | null;
    if (!el || el.tagName !== 'VIDEO') return;

    // Pause every other <video> element
    document.querySelectorAll('video').forEach((v) => {
      if (v !== el) {
        try { v.pause(); } catch {}
      }
    });
  };

  // Capture phase so it catches plays from custom players too
  document.addEventListener('play', onPlay, true);
  return () => document.removeEventListener('play', onPlay, true);
}, []);


 const fetchPosts = async () => {
  try {
    setIsFetching(true);

    const res = await api.get('/media/my-posts', {
      params: { page, limit: LIMIT },
    });

    const raw: any[] = Array.isArray(res.data)
      ? res.data
      : res.data?.posts || [];

    const normalized = raw.map((p: any) => ({
      isHidden: false,
      hiddenAt: null,
      ...p,
      media: normalizeMediaToEntries(p),
    }));

   setPosts(prev => {
  if (page === 1) return normalized.slice(-MAX_POSTS_IN_MEMORY);

  const merged = [...prev, ...normalized];
  const byId = new Map(merged.map(p => [p._id, p]));
  return Array.from(byId.values()).slice(-MAX_POSTS_IN_MEMORY);
});

    const serverHasMore =
      typeof res.data?.hasMore === 'boolean'
        ? Boolean(res.data.hasMore)
        : normalized.length === LIMIT;

    setHasMore(serverHasMore);

  } catch (err) {
    console.error('❌ Failed to fetch posts:', err);
  } finally {
    setIsFetching(false);
    setInitialLoading(false);
  }
};
useEffect(() => {
  const root = feedRef.current;
  if (!root) return;

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idx = Number(entry.target.getAttribute('data-index'));
          if (!isNaN(idx)) setActiveIndex(idx);
        }
      });
    },
    {
      root,
      threshold: 0.6,
    }
  );

  root.querySelectorAll('[data-index]').forEach(el => observer.observe(el));
  return () => observer.disconnect();
}, [posts]);
  
useEffect(() => {
  if (!posts.length) return;

  const upcoming = posts.slice(activeIndex, activeIndex + 2);

  upcoming.forEach(post => {
    post.media?.slice(0, 2).forEach(entry => {
      if (entry.kind === 'image') {
        if (prefetchedUrlsRef.current.has(entry.url)) return;
        prefetchedUrlsRef.current.add(entry.url);

        const img = new Image();
        img.src = entry.url;
      }

      if (entry.kind === 'video') {
        const src = resolveVideoUrl(entry);
        if (!src || prefetchedUrlsRef.current.has(src)) return;
        prefetchedUrlsRef.current.add(src);

        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = src;
        try { video.load(); } catch {}
      }
    });
  });
}, [posts, activeIndex]);



  const MetaRow = ({ post }: { post: Post }) => (
    <div style={metaRowStyle}>
      <div style={metaLeftStyle}>
      <span>
  {t('myPosts.meta.created', {
    datetime: new Date(post.createdAt).toLocaleString(),
    type: post.type.toUpperCase(), // or map type to a separate label
  })}
</span>

{(post.location?.city || post.location?.country) && (
  <span>
    {t('myPosts.meta.location', {
      city: post.location?.city || '',
      country: post.location?.country || '',
    })}
  </span>
)}

<button
  type="button"
  onClick={() => setLikesModalPostId(post._id)}
  style={likesButtonStyle}
  title={t('myPosts.meta.showLikesTooltip')}
>
❤️ {post.likes}
</button>

<button
  onClick={() => openPostViewer(post, 0)}
  disabled={!post.media || post.media.length === 0}
  style={{ ...presentationBtnStyle, opacity: (!post.media || post.media.length === 0) ? 0.5 : 1 }}
  title={
    (!post.media || post.media.length === 0)
      ? t('myPosts.meta.noMediaToOpen')
      : t('myPosts.meta.openViewer')
  }
>
  🎬 {t('myPosts.meta.presentation')}
</button>


      </div>
      </div>
  );

 const handleOpenMusic: (info: MusicInfo) => void | Promise<void> =
  async (info) => {
    if (info.musicId) {
      navigate(`/musicdetails/${info.musicId}`);
      return;
    }

    const q = (info.musicTitle || info.musicArtist || '').trim();
    if (!q) return;

    try {
      const res = await api.get('/music', { params: { q } });
      const list = Array.isArray(res.data) ? res.data : res.data?.tracks || [];
      if (list.length) {
        navigate(`/musicdetails/${list[0]._id}`);
      }
    } catch (e) {
      console.error('music lookup failed:', e);
    }
  };

const [hideBusy, setHideBusy] = useState<string | null>(null);

const handleHidePost = async (id: string) => {
  if (hideBusy) return;
  setHideBusy(id);
  try {
    const { data } = await api.post(`/post-visibility/${id}/hide`);
    setPosts(prev =>
      prev.map(p => p._id === id ? { ...p, isHidden: data.isHidden, hiddenAt: data.hiddenAt } : p)
    );
  } catch (e:any) {
    console.error('❌ Failed to hide post:', e?.response?.data || e.message);
    alert(e?.response?.data?.error || t('myPosts.errors.hideFailed'));
  } finally {
    setHideBusy(null);
  }
};

const handleUnhidePost = async (id: string) => {
  if (hideBusy) return;
  setHideBusy(id);
  try {
    const { data } = await api.post(`/post-visibility/${id}/unhide`);
    setPosts(prev =>
      prev.map(p => p._id === id ? { ...p, isHidden: data.isHidden, hiddenAt: data.hiddenAt } : p)
    );
  } catch (e:any) {
    console.error('❌ Failed to unhide post:', e?.response?.data || e.message);
    alert(e?.response?.data?.error || t('myPosts.errors.unhideFailed'));
  } finally {
    setHideBusy(null);
  }
};

const openPostViewer = (post: Post, index: number) => {
  navigate('/openviewer', {
    state: {
      media: post.media,
      startIndex: index,
    },
  });
};


 // Hide scrollbars globally for elements with .hScroll
 useEffect(() => {
  const style = document.createElement('style');
  style.setAttribute('data-hide-scrollbars', 'true');
  style.innerHTML = `
    /* Firefox + legacy IE/Edge */
    .hScroll { scrollbar-width: none; -ms-overflow-style: none; scrollbar-color: transparent transparent; }
    /* Chromium/WebKit */
    .hScroll::-webkit-scrollbar { display: none; width: 0 !important; height: 0 !important; }
    .hScroll::-webkit-scrollbar-thumb { background: transparent; }
  `;
  document.head.appendChild(style);
  return () => { document.head.removeChild(style); };
}, []);

  //Prefetching
  useEffect(() => {
  const cached = localStorage.getItem('prefetchedMyPosts');

  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      setPosts(parsed);
      localStorage.removeItem('prefetchedMyPosts');
      console.log("✅ Used prefetched posts in MyPostings");
    } catch {}
  }
}, []);

  
  useEffect(() => {
  fetchPosts();
}, [page]);


  useEffect(() => {
  if (!hasMore || isFetching) return;

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        setPage((p) => p + 1);
      }
    },
    {
      threshold: 0.7,
    }
  );

  const el = loadMoreRef.current;
  if (el) observer.observe(el);

  return () => {
    if (el) observer.unobserve(el);
    observer.disconnect();
  };
}, [hasMore, isFetching, posts]);


  useEffect(() => {
  (async () => {
    const res = await api.get('/media/my-posts', { params: { offset: 0, limit: 1 } });
    console.log('First my-posts item from API:', res.data?.[0]);
  })();
}, []);

useEffect(() => {
  if (!focusPostId) return;

  const el = document.querySelector<HTMLElement>(
    `[data-post-id="${focusPostId}"]`
  );
  if (!el) return;

  // ✅ instant jump to the post (no animation)
  el.scrollIntoView({
    behavior: 'auto',      // <- this is the important bit (was 'smooth')
    block: 'center',       // or 'start' if you want it at the very top
  });

  // Optional: highlight the card briefly so it's obvious
  el.style.outline = '2px solid #d1ac52';

  const timeout = window.setTimeout(() => {
    el.style.outline = 'none';
  }, 6000);

  return () => window.clearTimeout(timeout);
}, [focusPostId, posts]);



useEffect(() => {
  const styleId = 'spin-keyframes';

  // don't add twice
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;

  style.innerHTML = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  document.head.appendChild(style);

  return () => {
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();
  };
}, []);



  /**
   * Delete a post
   */
  const handleDelete = (post: Post) => {
  setDeleteTargetPost(post);
};

const confirmDelete = async () => {
  if (!deleteTargetPost) return;

  try {
    setDeleteBusy(true);
    await api.delete(`/media/${deleteTargetPost._id}`);

    setPosts(prev =>
      prev.filter(p => p._id !== deleteTargetPost._id)
    );

    setDeleteTargetPost(null);
  } catch (err: any) {
    console.error('❌ Failed to delete post:', err.response?.data || err.message);
    alert(t('myPosts.actions.deleteFailed'));
  } finally {
    setDeleteBusy(false);
  }
};

  
  const renderPostContent = (post: Post) => {
    const fullMedia = (post.media || []).map((entry, idx) => {
      const abs =
  entry.kind === 'video'
    ? resolveVideoUrl(entry)
    : entry.url;

      const video = entry.kind === 'video';
      const label = mediaLabelOf(entry);
      const hk = `${post._id}:${idx}`;
      const videoEntry = entry.kind === 'video' ? entry : null;

      return (
       <div
  key={abs || idx}
  style={mediaItemWrapStyle}
  role="group"
  tabIndex={-1}
>

 <AutoFitMedia
  url={abs}
  isVideo={entry.kind === 'video'}
  label={label}
  showMusic={hoveredKey === hk}
  musicId={videoEntry?.musicId ?? null}
  musicTitle={videoEntry?.musicTitle ?? null}
  musicArtist={videoEntry?.musicArtist ?? null}
  onMusicClick={handleOpenMusic}
  onMouseEnter={() => handleHoverEnter(hk)}
  onMouseLeave={handleHoverLeave}
/>

        </div>
      );
    });
  
    return (
      <>
        <div style={mediaContainerStyle} className="hScroll">{fullMedia}</div>
        <MetaRow post={post} />
      </>
    );
  };
  
return (
  <div ref={feedRef} style={verticalFeedStyle} data-my-postings-feed>
   {initialLoading ? (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      color: '#9ca3af',
      gap: 14,
    }}
  >
    <div
      style={{
        width: 26,
        height: 26,
        border: '3px solid rgba(255,255,255,0.15)',
        borderTop: '3px solid #cbd5f5',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }}
    />

    <div style={{ fontSize: 16 }}>
      {t('myPosts.loading') || 'Loading my posts…'}
    </div>
  </div>

) : posts.length === 0 ? (
  <p style={{ fontSize: 48, textAlign: 'center', marginTop: '40vh' }}>
    📭
  </p>
) : (

posts.map((post, index) => {

  const shouldRender =
    index >= activeIndex - WINDOW_BEFORE &&
    index <= activeIndex + WINDOW_AFTER;

  if (!shouldRender) {
    return (
      <div
        key={post._id}
        data-post-id={post._id}
        data-index={index}
        style={mobilePostCardStyle}
      />
    );
  }

  return (
    <div
      key={post._id}
      data-post-id={post._id}
      data-index={index}
      style={mobilePostCardStyle}
    >
      <div style={mobileMediaWrap}>
        {renderPostContent(post)}
      </div>

      <div style={mobileActionRow}>
        <button
          onClick={() =>
            navigate(`/posting/edit/${post._id}`, {
              state: {
                sidebarData: {
                  hashtags: [],
                  title: post.title || '',
                  titleColor: post.titleColor || '',
                },
              },
            })
          }
          style={mobileButton}
        >
          ✏️ {t('myPosts.actions.edit')}
        </button>

        <button
          onClick={() => handleDelete(post)}
          style={mobileDeleteButton}
        >
          🗑️ {t('myPosts.actions.delete')}
        </button>

        <button
          onClick={() =>
            post.isHidden
              ? handleUnhidePost(post._id)
              : handleHidePost(post._id)
          }
          disabled={hideBusy === post._id}
          style={{
            ...mobileButton,
            backgroundColor: post.isHidden ? '#1e824c' : '#333',
            opacity: hideBusy === post._id ? 0.6 : 1,
          }}
        >
          {post.isHidden
            ? `👀 ${t('myPosts.actions.unhide')}`
            : `🙈 ${t('myPosts.actions.hide')}`}
        </button>
      </div>
    </div>
  );
})
    )}
    <div ref={loadMoreRef} style={{ height: 1 }} />

    {/* Likes Modal */}
    {likesModalPostId && (
      <LikeListModal
        postId={likesModalPostId}
        onClose={() => setLikesModalPostId(null)}
      />
    )}

    {/* Delete Confirmation Modal */}
    {deleteTargetPost && (
      <div style={confirmOverlay}>
        <div style={confirmCard} onClick={(e) => e.stopPropagation()}>
          <div style={{ marginBottom: 14, fontSize: 16, fontWeight: 600 }}>
            {t('myPosts.actions.confirmDelete')}
          </div>

          <button onClick={confirmDelete} disabled={deleteBusy} style={dangerBtn}>
            {deleteBusy ? t('common.deleting') : t('common.confirm')}
          </button>

          <button
            onClick={() => setDeleteTargetPost(null)}
            disabled={deleteBusy}
            style={cancelBtn}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    )}
  </div>
);


};

// Styles
const MEDIA_HEIGHT = '78vh';
const LAND_MAX_HEIGHT = '86vh';          

const mediaContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',

  overflowX: 'auto',
  overflowY: 'visible',    
  
  gap: 12,
  padding: '8px 12px',
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',

  height: MEDIA_HEIGHT,
  flex: '0 0 auto',

  overscrollBehaviorX: 'contain',
  overscrollBehaviorY: 'auto',
};

const metaRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: '#777',
  fontSize: 13,
  marginTop: '0.1%',
  paddingRight: '2%',  // nudges the heart a bit to the right
  gap: 10,
  flexWrap: 'wrap',
};

const metaLeftStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};

const presentationBtnStyle: React.CSSProperties = {
  backgroundColor: '#000',
  color: '#bbb',
  border: '0.8px solid #333',
  borderRadius: 6,
  padding: '3px 8px',
  fontSize: 12,
  cursor: 'pointer',
  lineHeight: 1.2,
};

const mediaItemWrapStyle: React.CSSProperties = {
  flex: '0 0 auto',
  scrollSnapAlign: 'start',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  overflow: 'hidden', // ✅ prevent accidental vertical scroll
};


const likesButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#f97373',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  padding: '2px 6px',
  borderRadius: 999,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};

const confirmOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.85)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 99999999,
};

const confirmCard: React.CSSProperties = {
  width: "90%",
  maxWidth: 420,
  background: "#000",
  borderRadius: 16,
  padding: 22,
  textAlign: "center",
  border: "1px solid #333",
  color: "#e5e7eb",
  boxShadow: "0 25px 80px rgba(0,0,0,0.9)",
};

const dangerBtn: React.CSSProperties = {
  width: "100%",
  padding: 14,
  borderRadius: 12,
  border: "none",
  background: "#b91c1c",
  color: "#fff",
  fontWeight: 700,
  marginBottom: 10,
  cursor: "pointer",
};

const cancelBtn: React.CSSProperties = {
  width: "100%",
  padding: 12,
  borderRadius: 12,
  border: "1px solid #333",
  background: "transparent",
  color: "#cbd5f5",
  cursor: "pointer",
};

const mobilePostCardStyle: React.CSSProperties = {
  minHeight: '100vh',              // full screen
  scrollSnapAlign: 'start',     // snap per post
  width: '100%',
  background: '#000',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  display: 'flex',
  flexDirection: 'column',
};

const mobileMediaWrap: React.CSSProperties = {
  width: '100%',
  overflowX: 'auto',
  overflowY: 'visible',
  display: 'block',
};


const mobileActionRow: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  justifyContent: 'space-between',
  padding: '8px 10px 2px',
  flexWrap: 'wrap',
};

const mobileButton: React.CSSProperties = {
  flex: 1,
  padding: '6px 8px',
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.2)',
  background: '#111',
  color: '#fff',
  fontSize: 11,
  cursor: 'pointer',
};

const mobileDeleteButton: React.CSSProperties = {
  ...mobileButton,
  borderColor: '#b91c1c',
  color: '#f87171',
};

const verticalFeedStyle: React.CSSProperties = {
  height: '100vh',                 // 👈 important (mobile safe viewport)
  overflowY: 'auto',             // 👈 force scroll layer
  scrollSnapType: 'y mandatory',
  WebkitOverflowScrolling: 'touch',
  overscrollBehaviorY: 'contain',   // 👈 prevent lock
};


export default MyPostingsMobile; 
